import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { withLocalize } from 'react-localize-redux';
import toaster from '../utils/toaster';
import useUserMcpConnections from './hooks/useUserMcpConnections';
import UserMcpConnectionControl from './components/UserMcpConnectionControl';
import {
    CONNECT_RESULT_CONNECTED,
    ERROR_PARAM,
    GENERIC_ERROR_KEY,
    USER_MCP_URL_PARAMS,
    RESULT_PARAM,
    TOAST_AUTO_CLOSE_MS,
    connectErrorToastKey,
    toastKey,
} from './constants';

const UserMcpConnections = ({ translate }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { connections, busyProvider, refresh, connect, disconnect } = useUserMcpConnections();

    const notify = useCallback(
        (notifier, key) => notifier(translate(key), { autoClose: TOAST_AUTO_CLOSE_MS }),
        [translate]
    );

    const clearConnectParams = useCallback(() => {
        setSearchParams(previous => {
            const next = new URLSearchParams(previous);
            USER_MCP_URL_PARAMS.forEach(param => next.delete(param));
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const consumedParamsRef = useRef(null);

    useEffect(() => {
        const result = searchParams.get(RESULT_PARAM);
        const errorCode = searchParams.get(ERROR_PARAM);
        if (!result && !errorCode) return;

        const consumed = `${result}|${errorCode}`;
        if (consumedParamsRef.current === consumed) return;
        consumedParamsRef.current = consumed;
        // Cleared before anything async: these params are single use, so a reload must not repeat the toast.
        clearConnectParams();

        if (result === CONNECT_RESULT_CONNECTED) {
            notify(toaster.success, toastKey('CONNECTED'));
            refresh();
        } else if (errorCode) {
            notify(toaster.error, connectErrorToastKey(errorCode));
        }
    }, [searchParams, clearConnectParams, notify, refresh]);

    const handleConnect = async provider => {
        try {
            await connect(provider);
        } catch {
            notify(toaster.error, toastKey(GENERIC_ERROR_KEY));
        }
    };

    const handleDisconnect = async provider => {
        try {
            await disconnect(provider);
            notify(toaster.success, toastKey('DISCONNECTED'));
        } catch {
            notify(toaster.error, toastKey(GENERIC_ERROR_KEY));
        }
    };

    return connections.map(connection => (
        <UserMcpConnectionControl
            key={connection.provider}
            connection={connection}
            busy={busyProvider === connection.provider}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
        />
    ));
};

UserMcpConnections.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(UserMcpConnections);
